import React, {
  createRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { z } from "zod";
import { OBJECT_TYPE, SUBMISSION_TYPE_MODULE } from "common/constants";
import { buildLink } from "common/routing";
import { isNotNil } from "common/utils/tools";
import {
  getInfoPanelLinkForAttached,
  getObjectDisplayForAttached,
} from "common-client/utils/customObject";
import { SearchResultProps } from "common-client/utils/search";
import { getDefaultViewers } from "common-client/utils/submissions/visibilityPill";
import {
  GetPropertyForSubmissionQuery,
  useCreateSubmissionMutation,
  useGetCustomMapGeometryForSubmissionQuery,
  useGetOrCreateGuestPropertyLazyQuery,
  useGetOrCreatePropertyLazyQuery,
  useGetPropertyForSubmissionQuery,
  useGetSisdRuleDataQuery,
  useGetSubmissionQuery,
  useGetSubmissionTypeQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import useURLParsing from "../../hooks/useURLParsing";
import { colors } from "../../stitches.config";
import { setTitle } from "../../utils/title";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { Markdown } from "../Common/__styles__/Markdown";
import { Button } from "../Common/Buttons/Button";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import FullPageLoadingScreen from "../Common/FullPageLoadingScreen";
import AccessDeniedPage, {
  ACCESS_DENIED_RESOURCE,
} from "../Guest/AccessDeniedPage";
import { accountTitle as getAccountTitle } from "../Guest/utils";
import { ButtonContainer, Stack } from "./__styles__/Submission";
import useSubmissionsChatAgent, {
  createFormUpdateHandler,
} from "./ChatAgent/SubmissionsChatAgent";
import { FormComponent, FormDataType } from "./Form";
import SubmissionHeader from "./SubmissionHeader";
import SubmissionSuccess from "./SubmissionSuccess";
import {
  generateUpsertErrorMessage,
  submissionTypeContainsEcDocumentType,
} from "./utils";

interface CreateSubmissionParams {
  objectId?: string;
  objectType?: OBJECT_TYPE;
  submissionTypeId: string;
  taskId?: string;
  workflowStepId?: string;
}

export interface CreateSubmissionProps
  extends RouteComponentProps<CreateSubmissionParams> {}

type HistoryLocationState = {
  prevLocation?: string;
  search?: string;
  formData?: any;
  relatedSubmissionId?: string;
  isGlobal?: boolean;
};

type PropertyForSubmission = Pick<
  NonNullable<GetPropertyForSubmissionQuery["property"]>,
  "id" | "fullAddress" | "latitude" | "longitude"
>;

export const CreateSubmission = ({
  objectId,
  objectType,
  submissionTypeId,
  taskId,
  workflowStepId,
}: CreateSubmissionParams) => {
  const history = useHistory();
  const location = useLocation<Maybe<HistoryLocationState>>();
  const relatedSubmissionId = location.state?.relatedSubmissionId ?? null;
  const isGlobal = location.state?.isGlobal ?? false;
  const [disabled, setDisabled] = useState(false);
  const initialFormData = location.state?.formData;
  const [serverProvidedFormData, setServerProvidedFormData] =
    useState<FormDataType | null>(null);
  const hasRenderedForm = useRef(false);

  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const accountTitle = getAccountTitle();
  const { isPublic, account, publicUser } = useContext(AuthContext);
  const isGuest = isPublic && !publicUser;

  // property is undefined when it has never been set. If there is no property, it will be set to null
  const [property, setProperty] = useState<
    Maybe<PropertyForSubmission> | undefined
  >(undefined);

  const paramPropertyId =
    objectType === OBJECT_TYPE.PROPERTY ? objectId : undefined;
  const customMapGeometryId =
    objectType === OBJECT_TYPE.CUSTOM_MAP_GEOMETRY ? objectId : undefined;

  const {
    data,
    loading,
    error,
    refetch: refetchSubmissionType,
  } = useGetSubmissionTypeQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      submissionTypeId,
      propertyId: paramPropertyId,
      customMapGeometryId,
      workflowStepId,
    },
  });

  //we fill in default values from the property fields on this request
  //it needs to get refetched anytime the property changes
  useEffect(() => {
    if (property?.id && property.id !== paramPropertyId) {
      void refetchSubmissionType({
        submissionTypeId,
        propertyId: property.id,
        customMapGeometryId,
        workflowStepId,
      });
    }
  }, [
    property?.id,
    paramPropertyId,
    refetchSubmissionType,
    submissionTypeId,
    customMapGeometryId,
    workflowStepId,
  ]);

  const { loading: propertyLoading, refetch: refetchPropertyForSubmission } =
    useGetPropertyForSubmissionQuery({
      variables: { propertyId: objectId! },
      skip: !objectId || objectType !== OBJECT_TYPE.PROPERTY,
      onCompleted: data => setProperty(data?.property ?? null),
    });

  const { data: customMapGeometryData, loading: customMapGeometryLoading } =
    useGetCustomMapGeometryForSubmissionQuery({
      variables: { geometryId: objectId! },
      skip: !objectId || objectType !== OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
    });

  const { data: relatedSubmissionData, loading: relatedSubmissionLoading } =
    useGetSubmissionQuery({
      variables: {
        submissionId: relatedSubmissionId!,
        isPublic,
        isGuest,
      },
      skip: !relatedSubmissionId,
    });

  const submissionType = data?.submissionType;

  const { data: sisdRuleData, loading: sisdRuleDataLoading } =
    useGetSisdRuleDataQuery({
      variables: { propertyId: property?.id! },
      skip:
        !submissionType?.modules?.includes(
          SUBMISSION_TYPE_MODULE.SUBSTANTIAL_IMPROVEMENT_SUBSTANTIAL_DAMAGE,
        ) || !property?.id,
    });
  const [createSubmission, { loading: createLoading }] =
    useCreateSubmissionMutation({
      onCompleted: data => {
        const submission = data.createSubmission;
        const workflowId = submission.workflowStep?.workflow.id;
        track("Submission created", {
          submissionId: submission.id,
          workflowId,
          "Submission Category": submissionType?.category,
          "Submission Type": submissionType?.name,
          "is global": isGlobal,
        });

        if (isPublic) {
          if (workflowId) {
            if (submissionType?.successMessage) {
              addSuccessToast(submissionType.successMessage);
            }

            history.push({
              pathname: buildLink("workflow", {
                workflowId,
              }),
            });
          } else {
            setShowSubmissionSuccess(true);
          }
        } else {
          if (data.createSubmission.summaries?.length) {
            history.push({
              pathname: buildLink("submissionSummary", {
                submissionId: submission.id,
              }),
              state: { prevLocation },
            });
          } else {
            history.push({
              pathname: buildLink("editSubmission", {
                submissionId: submission.id,
              }),
              state: { prevLocation },
            });
          }
          if (submissionType?.successMessage) {
            addSuccessToast(submissionType.successMessage);
          }
        }
      },
      onError: error => {
        const message = generateUpsertErrorMessage(error);
        addErrorToast(message);
      },
    });

  const [getOrCreateProperty, { loading: propertyFromSearchLoading }] =
    useGetOrCreatePropertyLazyQuery({
      onError: () => {
        addErrorToast(
          "There was an issue finding a property with this address. Please provide a different address",
        );
      },
    });

  const [
    getOrCreateGuestProperty,
    { loading: guestPropertyFromSearchLoading },
  ] = useGetOrCreateGuestPropertyLazyQuery({
    onError: () => {
      addErrorToast(
        "There was an issue finding a property with this address. Please provide a different address",
      );
    },
  });

  const handleSearchResultClick = useCallback(
    async (result: SearchResultProps) => {
      if (result.propertyId) {
        const { data } = await refetchPropertyForSubmission({
          propertyId: result.propertyId,
        });
        setProperty(data?.property ?? null);
        return;
      }

      const variables = {
        address: result.address,
        latitude: result.point.latitude,
        longitude: result.point.longitude,
        parcelId: result.geocacheParcelId,
        improveParcelCoordinates: true,
      };

      const { data: createPropertyData } = await (
        isPublic ?
          getOrCreateGuestProperty
        : getOrCreateProperty)({
        variables,
      });

      if (!createPropertyData?.getProperty) {
        addErrorToast(
          "There was an issue finding a property with this address. Please provide a different address",
        );
      } else {
        setProperty(createPropertyData?.getProperty ?? null);
      }
    },
    [
      getOrCreateGuestProperty,
      getOrCreateProperty,
      isPublic,
      addErrorToast,
      refetchPropertyForSubmission,
    ],
  );

  const handleFormUpdate = useMemo(
    () =>
      createFormUpdateHandler({
        setServerProvidedFormData,
        setDisabled,
        addSuccessToast,
      }),
    [setDisabled, addSuccessToast, setServerProvidedFormData],
  );

  const chatAgentConfig = useMemo(
    () => ({
      isPublic,
      submissionTypeVersionId: data?.submissionType?.currentVersion?.id,
      formData: initialFormData ?? {},
      onFormUpdate: handleFormUpdate,
    }),
    [
      isPublic,
      data?.submissionType?.currentVersion?.id,
      initialFormData,
      handleFormUpdate,
    ],
  );

  const {
    ChatToggleButton,
    ChatPanel: SidePanel,
    isChatPanelOpen: isSidePanelOpen,
  } = useSubmissionsChatAgent(chatAgentConfig);

  if (
    !hasRenderedForm.current &&
    (loading ||
      propertyLoading ||
      customMapGeometryLoading ||
      relatedSubmissionLoading ||
      sisdRuleDataLoading)
  ) {
    return <FullPageLoadingScreen />;
  }

  //TODO: Replace with a proper error page
  if (
    error ||
    !data?.submissionType ||
    !data.submissionType.currentVersion.defaultValuesContext
  ) {
    return (
      <div style={{ color: colors.contentPrimaryInverted.toString() }}>
        Something went wrong
      </div>
    );
  }

  hasRenderedForm.current = true;

  if (!data.submissionType.currentUserCanCreate) {
    return <AccessDeniedPage resource={ACCESS_DENIED_RESOURCE.RECORD} />;
  }

  if (isPublic) {
    setTitle(`Create Record | ${accountTitle}`);
  }

  const submitFormRef = createRef<HTMLButtonElement>();
  const versionId = data.submissionType.currentVersion.id;

  const baseLocation = isPublic ? "guestHome" : "map";

  const prevLocation = `${
    location.state?.prevLocation ?? buildLink(baseLocation)
  }${location.state?.search ?? ""}`;

  const handleSubmit = async (formData: FormDataType) => {
    await createSubmission({
      variables: {
        data: {
          formData,
          versionId,
          relatedSubmissionId,
          ...(property || (objectId && objectType) ?
            {
              objectId: property?.id || objectId,
              objectType: objectType || OBJECT_TYPE.PROPERTY,
            }
          : {}),
          taskId,
          workflowStepId,
        },
        isPublic,
        isGuest,
      },
    });
  };

  const handleOnChange = ({
    documentUploadsInProgress = false,
    searchAddressCleared = false,
  }: {
    documentUploadsInProgress?: boolean;
    searchAddressCleared?: boolean;
  }) => {
    if (searchAddressCleared) {
      setProperty(null);
    } else {
      setDisabled(documentUploadsInProgress);
    }
  };

  const customMapGeometry = customMapGeometryData?.customMapGeometry;
  const searchForProperty =
    objectType === OBJECT_TYPE.PROPERTY &&
    !submissionTypeContainsEcDocumentType({
      submissionType: data.submissionType,
      accountDocumentTypes: account?.accountDocumentTypes,
    });

  const submitButtonDisabled =
    createLoading || disabled || (searchForProperty && !property);

  const rightContainer =
    !isPublic ?
      <ButtonContainer>
        {!isPublic && (
          <>
            <Button
              size="small"
              variant="primary"
              onPress={() => {
                submitFormRef.current!.click();
              }}
              isDisabled={submitButtonDisabled}
              isPending={createLoading}
              title="Submit"
            />
            {ChatToggleButton}
          </>
        )}
      </ButtonContainer>
    : undefined;

  let componentToDisplay: JSX.Element;
  if (showSubmissionSuccess) {
    componentToDisplay = (
      <SubmissionSuccess
        preamble={data.submissionType.preamble!}
        postamble={data.submissionType.postamble!}
      />
    );
  } else {
    componentToDisplay = (
      <FullPageFormLayout
        subtitle={`Add new ${data.submissionType.name}`}
        prevLocation={prevLocation}
        rightContainer={rightContainer}
        isSidePanelOpen={isSidePanelOpen}
        sidePanel={SidePanel}
        onSave={() => {
          submitFormRef.current!.click();
        }}
        buttonText="Submit"
        buttonDisabled={submitButtonDisabled}
        buttonLoading={createLoading}
      >
        <Stack main>
          <SubmissionHeader
            account={{
              logoUrl: account?.logoUrl ?? "",
              name: account?.name ?? "",
            }}
            submissionType={data.submissionType}
            objectDisplay={getObjectDisplayForAttached({
              objectType,
              property,
              customMapGeometry,
            })}
            objectInfoPanelLink={getInfoPanelLinkForAttached({
              objectType,
              property,
              customMapGeometry,
              isPublic,
            })}
            viewers={getDefaultViewers(
              data.submissionType.hiddenFromPublicByDefault,
              account?.name,
            )}
          >
            {data.submissionType.description && (
              <Markdown markdown={data.submissionType.description} />
            )}
          </SubmissionHeader>
          <FormComponent
            submissionType={data.submissionType}
            onSubmit={handleSubmit}
            onChange={handleOnChange}
            submitFormRef={submitFormRef}
            initialFormData={initialFormData}
            relatedSubmissions={[relatedSubmissionData?.submission].filter(
              isNotNil,
            )}
            dynamicDefaultValues={
              data.submissionType.currentVersion.defaultValuesContext
            }
            account={sisdRuleData?.account}
            property={{
              FIRMInfo: sisdRuleData?.FIRMInfo,
              fullAddress: property?.fullAddress || undefined,
            }}
            loadingFormPaths={{}}
            onSearchResultClick={
              searchForProperty ? handleSearchResultClick : undefined
            }
            onSearchClear={() => setProperty(null)}
            loadingSearchValue={
              propertyFromSearchLoading || guestPropertyFromSearchLoading
            }
            serverProvidedFormData={serverProvidedFormData ?? undefined}
          />
        </Stack>
      </FullPageFormLayout>
    );
  }

  return componentToDisplay;
};

const objectTypeQueryParam = z
  .object({
    objectType: z.enum(OBJECT_TYPE).optional(),
    taskId: z.string().optional(),
    workflowStepId: z.string().optional(),
  })
  .strict();

const CreateSubmissionContainer = ({ match }: CreateSubmissionProps) => {
  const { submissionTypeId, objectId } = match.params;
  const queryParams = useURLParsing({
    routeName: "createSubmission",
    parser: objectTypeQueryParam,
  });
  const objectType = queryParams?.objectType ?? OBJECT_TYPE.PROPERTY;
  const taskId = queryParams?.taskId;
  const workflowStepId = queryParams?.workflowStepId;

  return (
    <CreateSubmission
      key={submissionTypeId}
      submissionTypeId={submissionTypeId}
      objectId={objectId}
      objectType={objectType}
      taskId={taskId}
      workflowStepId={workflowStepId}
    />
  );
};

export default CreateSubmissionContainer;
